import { store } from '@/store';
import {
  CalculatePremiumRequest,
  PriceBreakdownRequest,
} from '@/types/PriceDetail';
import { ProductPlanPriceRequest } from '@/types/Product';
import { createAsyncThunk } from '@reduxjs/toolkit';

import { AddOn } from '@/enums';
import { getProductCode } from '@/helpers/ProductHelper';
import axiosInterceptorInstance from './axiosInterceptorInstance';

export const calculateTotalPremium = createAsyncThunk(
  'product/calculateTotalPremium',
  async () => {
    const rootState = store.getState();

    let totalPremium = 0;
    let discountPremium = 0;

    const selectedPlanPremium = rootState.uiState.productPremiumRate?.find(
      (data) => data.planClass == rootState.planSummary.planClass,
    );
    totalPremium += selectedPlanPremium?.netPremiumRate ?? 0;
    discountPremium += selectedPlanPremium?.discountAmount ?? 0;

    // Covid addon.
    if (rootState.planSummary.addonCoverages.covid19.isSelected) {
      const covidAddon = rootState.uiState.productAddonRate?.find(
        (data) => data.addOnId == AddOn.Covid,
      );
      totalPremium += covidAddon?.netAddOnRate ?? 0;
      discountPremium += covidAddon?.discountAmount ?? 0;
    }

    // Adventurous addon.
    if (rootState.planSummary.addonCoverages.adventurousActivities.isSelected) {
      const adventurousAddon = rootState.uiState.productAddonRate?.find(
        (data) =>
          data.addOnId == AddOn.Adventurous &&
          data.planClass == rootState.planSummary.planClass,
      );
      totalPremium += adventurousAddon?.netAddOnRate ?? 0;
      discountPremium += adventurousAddon?.discountAmount ?? 0;
    }

    return {
      totalPremium,
      discountPremium,
    };
  },
);

export const getProductArea = createAsyncThunk(
  'product/getProductArea',
  async ({ language }: { language: string }) => {
    try {
      const relativeUrl = 'ProductArea/GetProductAreaList/';
      const queryString = getProductCode();
      const apiUrl = `/product/?relativeUrl=${relativeUrl}&queryString=${queryString}/${language}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const getProductPlanClass = createAsyncThunk(
  'product/getProductPlanClass',
  async ({ language }: { language: string }) => {
    try {
      const relativeUrl = 'ProductPlanClass/GetProductPlanClassList/';
      const queryString = getProductCode();
      const apiUrl = `/product/?relativeUrl=${relativeUrl}&queryString=${queryString}/${language}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const getProductPlanType = createAsyncThunk(
  'product/getProductPlanType',
  async ({ tripType, language }: { tripType: number; language: string }) => {
    try {
      const relativeUrl = 'ProductPlanType/GetProductPlanTypeList/';
      const queryString = `${getProductCode()}/${tripType}`;
      const apiUrl = `/product/?relativeUrl=${relativeUrl}&queryString=${queryString}/${language}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const getProductPremiumRate = createAsyncThunk(
  'product/getProductPremiumRate',
  async (request: ProductPlanPriceRequest) => {
    try {
      const relativeUrl = 'ProductPremiumRate/GetTravelPremiumRate/?';
      const queryString = new URLSearchParams(request as any).toString();
      const apiUrl = `/product/?relativeUrl=${relativeUrl}&queryString=${encodeURIComponent(
        queryString,
      )}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const getTravelPlanPrice = createAsyncThunk(
  'product/getTravelPlanPrice',
  async (request: ProductPlanPriceRequest) => {
    try {
      const relativeUrl = 'ProductPremiumRate/GetTravelPlanPrice';
      const apiUrl = `/product/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const getTravelPlanPriceBreakdown = createAsyncThunk(
  'product/getTravelPlanPriceBreakdown',
  async (request: PriceBreakdownRequest) => {
    try {
      const relativeUrl = 'ProductPremiumRate/GetTravelPlanPriceBreakdown';
      const apiUrl = `/product/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const calculatePremium = createAsyncThunk(
  'quote/calculatePremium',
  async (request: CalculatePremiumRequest) => {
    try {
      const relativeUrl = 'Travel/CalculatePremium';
      const apiUrl = `/quote/?relativeUrl=${relativeUrl}`;
      const response = await axiosInterceptorInstance.post(apiUrl, request);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const getProductInfo = createAsyncThunk(
  'product',
  async (productId: string) => {
    try {
      const relativeUrl = 'Product/';
      const queryString = productId;
      const apiUrl = `/product/?relativeUrl=${relativeUrl}&queryString=${queryString}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);

export const getProduct = createAsyncThunk(
  'productStatus',
  async ({ language }: { language: string }) => {
    try {
      const relativeUrl = 'product/getstatus/';
      const queryString = getProductCode();
      const apiUrl = `/product/?relativeUrl=${relativeUrl}&queryString=${queryString}/${language}`;
      const response = await axiosInterceptorInstance.get(apiUrl);
      return response.data;
    } catch (error) {
      return error;
    }
  },
);
